import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe2, Download, FileSpreadsheet, FileText, Loader2, Ban } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";

const IT_MONTHS = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 2 + i);

function colorClass(score, target, na) {
  if (na) return "bg-slate-200 dark:bg-white/5 text-slate-500 dark:text-white/40 border border-transparent dark:border-white/10";
  if (score == null || target == null) return "";
  return score >= target
    ? "bg-emerald-100 dark:bg-emerald-500/25 text-emerald-800 dark:text-emerald-100 border border-transparent dark:border-emerald-400/60"
    : "bg-rose-100 dark:bg-rose-500/25 text-rose-800 dark:text-rose-100 border border-transparent dark:border-rose-400/60";
}

function Cell({ score, na, target, canEdit, onCommit, onToggleNa, testid }) {
  const [val, setVal] = useState(score == null ? "" : String(score));
  useEffect(() => { setVal(score == null ? "" : String(score)); }, [score]);
  const cls = colorClass(score, target, na);
  if (na) {
    return (
      <div className={`px-2 py-1.5 rounded-md text-center text-sm font-semibold flex items-center justify-center gap-1 ${cls}`} data-testid={testid}>
        <span>—</span>
        {canEdit && (
          <button className="text-[9px] text-slate-500 hover:text-slate-700 underline ml-1" onClick={() => onToggleNa(false)}>attiva</button>
        )}
      </div>
    );
  }
  if (!canEdit) {
    return (
      <div className={`px-2 py-1.5 rounded-md text-center text-sm font-semibold ${cls}`} data-testid={testid}>
        {score == null ? "—" : score.toFixed(1)}
      </div>
    );
  }
  return (
    <div className={`group/cell relative flex items-center gap-1 rounded-md ${cls}`}>
      <Input
        type="number" step="0.1" min="0" max="10"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => onCommit(val === "" ? null : parseFloat(val))}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="h-8 text-center px-1 border-0 bg-transparent focus:ring-1 focus:ring-lavender-400 font-semibold"
        data-testid={testid}
      />
      <button
        className="opacity-0 group-hover/cell:opacity-70 focus:opacity-100 transition-opacity text-[9px] text-slate-500 hover:text-rose-500 pr-2"
        onClick={() => onToggleNa(true)}
        title="Marca N/D"
      >
        <Ban className="h-3 w-3" />
      </button>
    </div>
  );
}

function Sparkline({ hotelId, metric, endYear, endMonth, target }) {
  const [series, setSeries] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/web-reputation/trend?hotel_id=${hotelId}&metric=${metric}&end_year=${endYear}&end_month=${endMonth}`)
      .then((r) => { if (!cancelled) setSeries(r.data.series); }).catch(() => {});
    return () => { cancelled = true; };
  }, [hotelId, metric, endYear, endMonth]);
  if (!series) return <div className="h-8 w-24" />;
  const stroke = target && series.slice(-1)[0]?.value != null && series.slice(-1)[0].value >= target
    ? "#10b981" : series.slice(-1)[0]?.value == null ? "#94a3b8" : "#f43f5e";
  return (
    <div className="h-8 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 4, right: 2, bottom: 2, left: 2 }}>
          <YAxis hide domain={["auto", "auto"]} />
          <Tooltip content={({ active, payload }) => active && payload?.[0] ? (
            <div className="rounded bg-slate-800 text-white text-[10px] px-1.5 py-0.5">
              {payload[0].payload.label}: {payload[0].value?.toFixed?.(1) ?? "—"}
            </div>
          ) : null} />
          <Line type="monotone" dataKey="value" stroke={stroke} strokeWidth={1.5} dot={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function WebReputation() {
  const { user } = useAuth();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/web-reputation?year=${year}&month=${month}`)
      .then((r) => {
        setData(r.data);
        if (!selectedHotel && r.data.hotels?.[0]) setSelectedHotel(r.data.hotels[0].id);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year, month]);

  const canEdit = data?.can_edit;
  const summary = useMemo(() => {
    if (!data || !selectedHotel) return null;
    return data.rows.find((r) => r.hotel_id === selectedHotel);
  }, [data, selectedHotel]);

  const commit = async (hotelId, patch) => {
    try {
      await api.put(`/web-reputation/${hotelId}`, { year, month, ...patch });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doExport = async (fmt) => {
    const oneHotel = user?.role !== "super_admin" ? `&hotel_id=${(user?.hotel_ids || [])[0] || ""}` : "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/web-reputation/export/${fmt}?year=${year}&month=${month}${oneHotel}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = `WebReputation_${IT_MONTHS[month]}_${year}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      toast.success(`Export ${fmt.toUpperCase()} scaricato`);
    } catch { toast.error("Errore export"); }
  };

  return (
    <div className="space-y-5" data-testid="web-reputation-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display tracking-tight flex items-center gap-2 text-2xl md:text-3xl">
            <Globe2 className="h-6 w-6 text-lavender-600" /> Web Reputation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Booking · Staff · Google per hotel, con confronto {year - 1} vs {year} e trend 12 mesi.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Anno</div>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-[110px]" data-testid="wr-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Mese</div>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="wr-month"><SelectValue /></SelectTrigger>
              <SelectContent>{IT_MONTHS.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="wr-export-xlsx" className="gap-1.5">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="wr-export-pdf" className="gap-1.5">
            <FileText className="h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      <Card className="p-4 overflow-x-auto">
        {loading || !data ? (
          <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 inline animate-spin mr-2" />Caricamento…</div>
        ) : data.rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">Nessun hotel disponibile.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th rowSpan={2} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground">Hotel</th>
                <th colSpan={2} className="px-3 py-1 text-center text-[10px] uppercase tracking-[0.16em] text-lavender-700 dark:text-lavender-300 bg-lavender-50 dark:bg-lavender-950/30">Booking</th>
                <th className="px-3 py-1 text-center text-[10px] uppercase tracking-[0.16em] text-lavender-700 dark:text-lavender-300 bg-lavender-50 dark:bg-lavender-950/30">Staff</th>
                <th colSpan={2} className="px-3 py-1 text-center text-[10px] uppercase tracking-[0.16em] text-lavender-700 dark:text-lavender-300 bg-lavender-50 dark:bg-lavender-950/30">Google</th>
                <th rowSpan={2} className="px-3 py-2 text-center text-[10px] uppercase tracking-wider text-muted-foreground">Trend Booking</th>
              </tr>
              <tr className="border-b border-border/60 text-xs text-muted-foreground">
                <th className="px-2 py-1 font-normal">{year - 1}</th>
                <th className="px-2 py-1 font-normal">{year}</th>
                <th className="px-2 py-1 font-normal">{year}</th>
                <th className="px-2 py-1 font-normal">{year - 1}</th>
                <th className="px-2 py-1 font-normal">{year}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.hotel_id} onClick={() => setSelectedHotel(r.hotel_id)}
                    className={`border-b border-border/40 cursor-pointer transition-colors ${
                      selectedHotel === r.hotel_id
                        ? "bg-lavender-50/70 dark:bg-transparent ring-2 ring-inset ring-lavender-400 dark:ring-purple-400/70 relative z-10"
                        : "hover:bg-accent/30 dark:hover:bg-white/[0.03]"
                    }`}
                    data-testid={`wr-row-${r.hotel_id}`}>
                  <td className="px-3 py-2 font-medium">{r.hotel_nome}</td>
                  <td className="px-1.5 py-1.5">
                    <Cell score={r.prev.booking_score} na={r.prev.booking_na} target={r.targets.booking_target}
                      canEdit={canEdit} testid={`wr-cell-b-prev-${r.hotel_id}`}
                      onCommit={(v) => commit(r.hotel_id, { year: year - 1, month, booking_score: v })}
                      onToggleNa={(na) => commit(r.hotel_id, { year: year - 1, month, booking_na: na })} />
                  </td>
                  <td className="px-1.5 py-1.5">
                    <Cell score={r.curr.booking_score} na={r.curr.booking_na} target={r.targets.booking_target}
                      canEdit={canEdit} testid={`wr-cell-b-curr-${r.hotel_id}`}
                      onCommit={(v) => commit(r.hotel_id, { booking_score: v })}
                      onToggleNa={(na) => commit(r.hotel_id, { booking_na: na })} />
                  </td>
                  <td className="px-1.5 py-1.5">
                    <Cell score={r.curr.staff_score} na={r.curr.staff_na} target={r.targets.staff_target}
                      canEdit={canEdit} testid={`wr-cell-s-curr-${r.hotel_id}`}
                      onCommit={(v) => commit(r.hotel_id, { staff_score: v })}
                      onToggleNa={(na) => commit(r.hotel_id, { staff_na: na })} />
                  </td>
                  <td className="px-1.5 py-1.5">
                    <Cell score={r.prev.google_score} na={r.prev.google_na} target={r.targets.google_target}
                      canEdit={canEdit} testid={`wr-cell-g-prev-${r.hotel_id}`}
                      onCommit={(v) => commit(r.hotel_id, { year: year - 1, month, google_score: v })}
                      onToggleNa={(na) => commit(r.hotel_id, { year: year - 1, month, google_na: na })} />
                  </td>
                  <td className="px-1.5 py-1.5">
                    <Cell score={r.curr.google_score} na={r.curr.google_na} target={r.targets.google_target}
                      canEdit={canEdit} testid={`wr-cell-g-curr-${r.hotel_id}`}
                      onCommit={(v) => commit(r.hotel_id, { google_score: v })}
                      onToggleNa={(na) => commit(r.hotel_id, { google_na: na })} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Sparkline hotelId={r.hotel_id} metric="booking" endYear={year} endMonth={month} target={r.targets.booking_target} />
                  </td>
                </tr>
              ))}
              <tr className="bg-lavender-100/60 dark:bg-purple-950/70 font-semibold border-t border-lavender-200/60 dark:border-purple-800/60">
                <td className="px-3 py-2 uppercase text-[11px] tracking-wider text-lavender-800 dark:text-white/90">Media Gruppo · {IT_MONTHS[month]}</td>
                <td className="px-2 py-2 text-center text-muted-foreground dark:text-white/40">—</td>
                <td className="px-2 py-2 text-center dark:text-white/90">{data.averages.booking?.toFixed(1) ?? "—"}</td>
                <td className="px-2 py-2 text-center dark:text-white/90">{data.averages.staff?.toFixed(1) ?? "—"}</td>
                <td className="px-2 py-2 text-center text-muted-foreground dark:text-white/40">—</td>
                <td className="px-2 py-2 text-center dark:text-white/90">{data.averages.google?.toFixed(1) ?? "—"}</td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      {/* Card riepilogo hotel */}
      {summary && (
        <Card className="p-5" data-testid="wr-summary-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-lavender-700 dark:text-lavender-300 font-semibold">Riepilogo mensile</div>
              <h2 className="font-display text-xl font-semibold">{summary.hotel_nome} · {IT_MONTHS[month]} {year}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: "booking", label: "Booking", target: summary.targets.booking_target, score: summary.curr.booking_score, na: summary.curr.booking_na, avg: data.averages.booking },
              { key: "staff", label: "Staff", target: summary.targets.staff_target, score: summary.curr.staff_score, na: summary.curr.staff_na, avg: data.averages.staff },
              { key: "google", label: "Google", target: summary.targets.google_target, score: summary.curr.google_score, na: summary.curr.google_na, avg: data.averages.google },
            ].map((m) => {
              const cls = colorClass(m.score, m.target, m.na);
              const ok = m.score != null && m.target != null && m.score >= m.target;
              return (
                <div key={m.key} className="rounded-xl border border-border/60 p-4 bg-background/40" data-testid={`wr-summary-${m.key}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{m.label}</div>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-xs text-muted-foreground">Obiettivo</div>
                      <div className="font-mono text-lg">{m.target != null ? m.target.toFixed(1) : "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Risultato</div>
                  <div className={`font-mono text-lg font-semibold px-2 py-0.5 rounded ${cls}`}>
                        {m.na ? "N/D" : m.score != null ? m.score.toFixed(1) : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground flex items-center justify-between">
                    <span>Media gruppo: <strong>{m.avg?.toFixed?.(1) ?? "—"}</strong></span>
                    {m.score != null && m.target != null && (
                      <span className={ok ? "text-emerald-600" : "text-rose-600"}>{ok ? "▲ obiettivo raggiunto" : "▼ sotto obiettivo"}</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <Sparkline hotelId={summary.hotel_id} metric={m.key} endYear={year} endMonth={month} target={m.target} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {canEdit && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Download className="h-3 w-3" /> Doppio-tap in cella per editare, Invio o click fuori per salvare. Icona vieto per marcare N/D.
        </p>
      )}
    </div>
  );
}
